import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import { trackEditorialCommerce } from '../../services/editorialRevenueTracking';
import './Editorials.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faHeart,
  faUserCircle,
  faShoppingBag,
  faPaperPlane,
  faBookmark,
  faFilter,
  faNewspaper,
  faExternalLinkAlt,
  faArrowUp,
  faArrowDown,
  faArrowsUpDown
} from '@fortawesome/free-solid-svg-icons';

// REMOVED: Static import - now using JSON fallback
// import editorials from './editorialsData.js';

const Editorials = () => {
  // Router hooks
  const location = useLocation();
  const navigate = useNavigate();
  
  // User context for Digital Card integration
  const { saveItem, isSaved, user } = useContext(UserContext);
  
  // Editorial data state
  const [allEditorials, setAllEditorials] = useState([]);
  const [displayEditorials, setDisplayEditorials] = useState([]);
  const [visibleEditorials, setVisibleEditorials] = useState([]);
  const [featuredEditorials, setFeaturedEditorials] = useState([]);
  const [topics, setTopics] = useState(['All']);
  const [selectedTopic, setSelectedTopic] = useState('All');
  
  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [screenSize, setScreenSize] = useState('large');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [sortOption, setSortOption] = useState('featured');
  const [scrollDirection, setScrollDirection] = useState('up');
  const [currentScrollY, setCurrentScrollY] = useState(0);
  const [showBottomNav, setShowBottomNav] = useState(false);
  
  // Video references for boomerang effect
  const leftVideoRef = useRef(null);
  const rightVideoRef = useRef(null);
  
  // Search management
  const searchTimeoutRef = useRef(null);
  const lastSearchQueryRef = useRef('');
  const lastSearchTopicRef = useRef('All');
  
  // Pagination
  const editorialsPerPage = 12;
  const observer = useRef();
  const loadingMoreRef = useRef(false);
  
  // Debug mode
  const debug = false;
  
  // Fallback image
  const fallbackImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='272' height='408'%3E%3Crect width='272' height='408' fill='%23f5f5f5'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E";

  // Boomerang video effect setup
  const setupBoomerangEffect = useCallback((videoElement) => {
    if (!videoElement) return;
    
    let isReversing = false;
    let cleanupFunctions = [];
    
    const handleTimeUpdate = () => {
      if (isReversing) {
        if (videoElement.currentTime <= 0.1) {
          isReversing = false;
          videoElement.currentTime = 0;
          videoElement.play().catch(console.error);
        }
      }
    };
    
    const handleEnded = () => {
      if (!isReversing) {
        isReversing = true;
        videoElement.currentTime = videoElement.duration - 0.1;
        
        const reverseInterval = setInterval(() => {
          if (videoElement.currentTime <= 0.1) {
            clearInterval(reverseInterval);
            isReversing = false;
            videoElement.currentTime = 0;
            videoElement.play().catch(console.error);
          } else {
            videoElement.currentTime -= 0.033;
          }
        }, 33);
        
        cleanupFunctions.push(() => clearInterval(reverseInterval));
      }
    };
    
    const handleCanPlayThrough = () => {
      videoElement.play().catch(console.error);
    };
    
    const handleLoadedData = () => {
      videoElement.playbackRate = 0.6;
      videoElement.style.imageRendering = 'auto';
      videoElement.style.filter = 'contrast(1.05) saturate(1.05) brightness(1.02)';
      videoElement.play().catch(console.error);
    };
    
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('ended', handleEnded);
    videoElement.addEventListener('canplaythrough', handleCanPlayThrough);
    videoElement.addEventListener('loadeddata', handleLoadedData);
    
    if (videoElement.readyState >= 3) {
      handleLoadedData();
    }
    
    const cleanup = () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('ended', handleEnded);
      videoElement.removeEventListener('canplaythrough', handleCanPlayThrough);
      videoElement.removeEventListener('loadeddata', handleLoadedData);
      cleanupFunctions.forEach(fn => fn());
    };
    
    cleanupFunctions.push(cleanup);
    return cleanup;
  }, []);

  // Setup videos when refs are available
  useEffect(() => {
    return () => {
      if (leftVideoRef.current) {
        leftVideoRef.current.pause();
      }
      if (rightVideoRef.current) {
        rightVideoRef.current.pause();
      }
    };
  }, []);

  // Handle scroll direction for bottom nav
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY > currentScrollY) {
        setScrollDirection('down');
        setShowBottomNav(false);
      } else {
        setScrollDirection('up');
        setShowBottomNav(scrollY > 100);
      }
      setCurrentScrollY(scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentScrollY]);

  // Handle responsive screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 600) {
        setScreenSize('small');
      } else if (width < 1050) {
        setScreenSize('medium');
      } else {
        setScreenSize('large');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Toggle filters for mobile
  const toggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  // Digital Card preference scoring function
  const scoreContentAgainstProfile = (item, userProfile) => {
    if (!userProfile?.curationSignature?.signalWeights) {
      return { score: 0.5, passesThreshold: true };
    }

    const signalWeights = userProfile.curationSignature.signalWeights;
    const threshold = userProfile.curationSignature.scoreThreshold || 0.6;
    
    const itemSignals = [
      ...(Array.isArray(item.signals) ? item.signals : []),
      ...(Array.isArray(item.tags) ? item.tags : []),
      ...(Array.isArray(item.keywords) ? item.keywords : []),
      ...(Array.isArray(item.hiddenKeywords) ? item.hiddenKeywords : []),
      item.topic,
      item.author
    ].filter(Boolean).map(s => s.toLowerCase());

    let totalScore = 0;
    let maxPossibleScore = 0;
    let matchCount = 0;

    Object.entries(signalWeights).forEach(([signal, weight]) => {
      const signalLower = signal.toLowerCase();
      maxPossibleScore += Math.abs(weight);
      
      if (itemSignals.some(itemSignal => itemSignal.includes(signalLower) || signalLower.includes(itemSignal))) {
        totalScore += weight;
        matchCount++;
      }
    });

    const normalizedScore = maxPossibleScore > 0 ? 
      Math.max(0, (totalScore + maxPossibleScore) / (2 * maxPossibleScore)) : 
      0.5;

    return {
      score: normalizedScore,
      passesThreshold: normalizedScore >= threshold,
      matchCount
    };
  };

  // Enhanced search with Digital Card preference integration
  const performSearch = useCallback(async (query, topic, allEditorials) => {
    setIsSearching(true);
    
    let filteredList = allEditorials;
    
    if (topic !== 'All') {
      filteredList = filteredList.filter(editorial => 
        editorial.topic === topic
      );
    }
    
    if (query && query.trim()) {
      const searchTerms = query.toLowerCase().trim().split(/\s+/).filter(term => term.length > 1);
      
      filteredList = filteredList.filter(editorial => {
        const searchableFields = [
          editorial.title || '',
          editorial.description || '',
          editorial.author || '',
          editorial.topic || '',
          ...(Array.isArray(editorial.hiddenKeywords) ? editorial.hiddenKeywords : []),
          ...(Array.isArray(editorial.tags) ? editorial.tags : []),
          ...(Array.isArray(editorial.signals) ? editorial.signals : []),
          ...(Array.isArray(editorial.keywords) ? editorial.keywords : [])
        ];
        
        const searchText = searchableFields.join(' ').toLowerCase();
        
        let keywordScore = 0;
        let matchedTerms = 0;
        
        searchTerms.forEach(term => {
          if (searchText.includes(term)) {
            matchedTerms++;
            
            if ((editorial.title || '').toLowerCase().includes(term) || 
                (editorial.topic || '').toLowerCase().includes(term)) {
              keywordScore += 10;
            } else {
              keywordScore += 5;
            }
          }
        });
        
        const matchRatio = matchedTerms / searchTerms.length;
        if (matchRatio < 0.6) {
          editorial.searchScore = 0;
          return false;
        }
        
        const { score: preferenceScore } = user ? 
          scoreContentAgainstProfile(editorial, user) : 
          { score: 0.5 };
        
        const preferenceMultiplier = 0.5 + (preferenceScore * 1.0);
        editorial.searchScore = keywordScore * preferenceMultiplier;
        editorial.preferenceScore = preferenceScore;
        
        return true;
      });
      
      filteredList.sort((a, b) => (b.searchScore || 0) - (a.searchScore || 0));
    } else {
      if (user?.curationSignature?.signalWeights && 
          Object.keys(user.curationSignature.signalWeights).length > 0) {
        
        filteredList = filteredList.map(editorial => {
          const { score: preferenceScore } = scoreContentAgainstProfile(editorial, user);
          editorial.preferenceScore = preferenceScore;
          editorial.searchScore = preferenceScore * 100;
          return editorial;
        });
        
        filteredList.sort((a, b) => (b.preferenceScore || 0) - (a.preferenceScore || 0));
      }
    }
    
    setDisplayEditorials(filteredList);
    setVisibleEditorials(filteredList.slice(0, editorialsPerPage));
    setIsSearching(false);
  }, [editorialsPerPage, user]);

  // Debounced search trigger
  const triggerSearch = useCallback((query, topic) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      if (query !== lastSearchQueryRef.current || topic !== lastSearchTopicRef.current) {
        lastSearchQueryRef.current = query;
        lastSearchTopicRef.current = topic;
        performSearch(query, topic, allEditorials);
      }
    }, 300);
  }, [performSearch, allEditorials]);

  // Get search query from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('q');
    if (searchParam && searchParam !== searchQuery) {
      setSearchQuery(searchParam);
    }
  }, [location.search, searchQuery]);

  // Header search listener
  useEffect(() => {
    const handleHeaderSearch = (event) => {
      const searchTerm = event.detail?.searchTerm || '';
      
      if (searchTerm === searchQuery) return;
      
      setSearchQuery(searchTerm);
      
      const newUrl = new URL(window.location);
      if (searchTerm.trim()) {
        newUrl.searchParams.set('q', searchTerm);
      } else {
        newUrl.searchParams.delete('q');
      }
      navigate(newUrl.pathname + newUrl.search, { replace: true });
    };

    window.addEventListener('artifex-search', handleHeaderSearch);
    return () => window.removeEventListener('artifex-search', handleHeaderSearch);
  }, [navigate, searchQuery]);

  const handleTopicChange = useCallback((topic) => {
    setSelectedTopic(topic);
  }, []);

  // Infinite scroll
  const addMoreEditorialsFn = useCallback(() => {
    if (loadingMoreRef.current || visibleEditorials.length >= displayEditorials.length) return;
    
    loadingMoreRef.current = true;
    
    setTimeout(() => {
      const currentCount = visibleEditorials.length;
      const nextBatch = displayEditorials.slice(currentCount, currentCount + editorialsPerPage);
      setVisibleEditorials(prev => [...prev, ...nextBatch]);
      loadingMoreRef.current = false;
    }, 100);
  }, [displayEditorials, visibleEditorials, editorialsPerPage]);

  const [shouldLoadMore, setShouldLoadMore] = useState(false);

  useEffect(() => {
    if (shouldLoadMore) {
      addMoreEditorialsFn();
      setShouldLoadMore(false);
    }
  }, [shouldLoadMore, addMoreEditorialsFn]);

  const lastEditorialRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && displayEditorials.length > visibleEditorials.length) {
        setShouldLoadMore(true);
      }
    });
    
    if (node) observer.current.observe(node);
  }, [displayEditorials, visibleEditorials]);

  // OPTIMIZED: Load editorials using JSON fallback for iOS performance
  useEffect(() => {
    const fetchEditorials = async () => {
      setIsLoading(true);
      try {
        // Skip Firebase - go straight to JSON fallback for faster iOS loading
        throw new Error('Using JSON fallback for faster iOS loading');
        
      } catch (err) {
        console.log('🔄 Loading fallback data from editorials.json...');
        
        // Fallback to JSON file when Firebase fails/times out
        try {
          const response = await fetch('/editorials.json');
          const jsonData = await response.json();
          
          console.log(`📁 Loaded ${jsonData.length} editorials from JSON fallback`);
          
          // Process JSON data (editorials don't need image URL normalization like discover)
          const processedEditorials = jsonData || [];
          
          setAllEditorials(processedEditorials);
          
          // Build topics
          const topicSet = new Set(processedEditorials.map(e => e.topic).filter(Boolean));
          setTopics(['All', ...Array.from(topicSet)]);
          
          // Featured editorials
          const featuredList = processedEditorials.filter(e => e.featured);
          setFeaturedEditorials(featuredList);
          
          // Initial display
          setDisplayEditorials(processedEditorials);
          setVisibleEditorials(processedEditorials.slice(0, editorialsPerPage));
          
          setError(null); // Clear error since fallback worked
          
        } catch (jsonError) {
          console.error('❌ Both Firebase and JSON failed:', jsonError);
          setError('Failed to load editorials from both Firebase and local data');
          setAllEditorials([]);
        }
        
        setIsLoading(false);
      }
    };
    
    fetchEditorials();
  }, [editorialsPerPage]);

  // Search trigger
  useEffect(() => {
    if (allEditorials.length > 0) {
      triggerSearch(searchQuery, selectedTopic);
    }
  }, [searchQuery, selectedTopic, allEditorials, triggerSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Window resize listener for control bar
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Enhanced save action with revenue tracking
  const handleSave = useCallback((e, editorial) => {
    e.preventDefault();
    e.stopPropagation();
    if (saveItem) {
      saveItem(editorial.id);
      
      trackEditorialCommerce(editorial.id, null, 'save', {
        userId: user?.uid || 'anonymous',
        source: 'editorial_save'
      });
      
      if (debug) console.log('Saved editorial:', editorial.title || editorial.id);
    }
  }, [saveItem, debug, user]);

  // Handle share action
  const handleShare = useCallback(async (e, editorial) => {
    e.preventDefault();
    e.stopPropagation();

    const shareData = {
      title: editorial.title,
      text: editorial.description,
      url: window.location.href
    };

    try {
      // Immediately trigger share for best responsiveness
      if (navigator.share) {
        await navigator.share(shareData);
        // Track successful share
        trackEditorialCommerce(editorial.id, null, 'share', {
          userId: user?.uid || 'anonymous',
          source: 'editorial_share'
        });
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch (error) {
      // Silently handle user dismissal
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
      }
    }
  }, [user]);

  // Enhanced read more with tracking
  const handleReadMore = useCallback((e, editorial) => {
    e.preventDefault();
    e.stopPropagation();
    
    trackEditorialCommerce(editorial.id, null, 'read_more', {
      userId: user?.uid || 'anonymous',
      source: 'editorial_read_more'
    });
    
    navigate(`/editorials/${editorial.id}`);
  }, [navigate, user]);

  // Loading state
  if (isLoading && !visibleEditorials.length) {
    return (
      <div className="editorials-loading" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '34vh',
        gap: '1rem'
      }}>
        <div className="loading-spinner" style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #0d47a1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p>Loading editorials...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="editorials-error" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '34vh',
        gap: '1rem',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <h2>Error loading editorials</h2>
        <p style={{ color: '#666' }}>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#0d47a1',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Top navigation menu - show only above 576px width */}
      {windowWidth >= 576 && (
        <div style={{ 
          marginTop: '60px',
          width: '100%', 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'white',
          borderBottom: '1px solid #eee',
          padding: '0.5rem 0'
        }}>
          <div style={{ 
           maxWidth: '750px',
           width: '100%',
           display: 'flex',
           justifyContent: 'space-between',
           alignItems: 'center'
         }}>
           <Link
             to="/discover"
             style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               flexDirection: 'column',
               padding: '0.5rem 1rem',
               background: 'transparent',
               color: '#666',
               textDecoration: 'none',
               borderRadius: '6px',
               minWidth: '80px',
               whiteSpace: 'nowrap',
               overflow: 'hidden'
             }}
           >
             <FontAwesomeIcon
               icon={faHome}
               style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}
             />
             <span style={{ fontSize: '0.8rem' }}>Discover</span>
           </Link>

        <Link
          to="/signals"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: '0.5rem 1rem',
            background: 'transparent',
            color: '#666',
            textDecoration: 'none',
            borderRadius: '6px',
            minWidth: '80px',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }}
        >
          <FontAwesomeIcon
            icon={faShoppingBag}
            style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}
          />
          <span style={{ fontSize: '0.8rem' }}>Signals</span>
        </Link>

        <Link
          to="/editorials"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: '0.5rem 1rem',
            background: '#0d47a1',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            minWidth: '80px',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }}
        >
          <FontAwesomeIcon
            icon={faNewspaper}
            style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}
          />
          <span style={{ fontSize: '0.8rem' }}>Editorials</span>
        </Link>
      </div>
    </div>
  )}
  
  {/* Mobile filters drawer */}
  {windowWidth < 1300 && showFilters && (
    <div style={{
      position: 'fixed',
      top: '60px',
      left: 0,
      right: 0,
      background: 'white',
      zIndex: 900,
      padding: '1rem',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      transition: 'transform 0.3s ease',
      transform: showFilters ? 'translateY(0)' : 'translateY(-100%)'
    }}>
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Topics</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        {topics.map((topic, index) => (
          <button 
            key={index}
            onClick={() => {
              handleTopicChange(topic);
              setShowFilters(false);
            }}
            style={{
              padding: '0.5rem 1rem',
              background: selectedTopic === topic ? '#0d47a1' : '#f5f5f5',
              color: selectedTopic === topic ? 'white' : '#333',
              border: 'none',
              borderRadius: '20px',
              fontSize: '0.8rem',
              cursor: 'pointer'
            }}
          >
            {topic}
          </button>
        ))}
      </div>
      
      <button 
        onClick={() => setShowFilters(false)}
        style={{
          display: 'block',
          width: '100%',
          padding: '0.5rem',
          background: '#f5f5f5',
         border: 'none',
         borderRadius: '4px',
         fontSize: '0.9rem',
         cursor: 'pointer'
       }}
     >
       Close Filters
     </button>
   </div>
 )}
 
 {/* Add spacing when menu is hidden */}
 {windowWidth < 576 && <div style={{ marginTop: '60px' }}></div>}
 
 {/* Main content container */}
 <div className="editorials-container" style={{ 
   paddingTop: windowWidth >= 576 ? '20px' : '20px'
 }}>
        {/* LEFT SIDEBAR - KEEP VIDEO BACKGROUND, FIX CONTAINER */}
        {windowWidth >= 1300 && (
          <>
            {/* Left Sidebar Video Background - KEEP EXACTLY AS IS */}
            <div style={{
              position: 'fixed',
              left: '0px',
              top: 0,
              width: '210px',
              height: '100vh',
              zIndex: 1,
              overflow: 'hidden'
            }}>
              <video
                ref={leftVideoRef}
                src="/videos/sidebar-column.mov"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
                muted
                loop={false}
                playsInline
                preload="metadata"
                autoPlay
                onLoadedData={() => {
                  if (leftVideoRef.current) {
                    setupBoomerangEffect(leftVideoRef.current);
                  }
                }}
                onError={(e) => {
                  console.error('Left video error:', e);
                }}
              />
            </div>
            
            {/* Left Sidebar Content */}
            <div className="editorials-left-sidebar" style={{
              position: 'sticky',
              top: '140px',
              width: '190px',
              height: '650px',
              marginLeft: 'calc(20px - 50vw + 50%)',
              zIndex: 2,
              backdropFilter: 'blur(3px)',
              WebkitBackdropFilter: 'blur(3px)'
            }}>
              {/* Content Container */}
              <div style={{
                position: 'relative',
                zIndex: 2,
                background: 'rgba(255, 255, 255, 0.3)',
                marginLeft: '10px',
                marginRight: '10px',
                marginTop: '10px',
                marginBottom: '20px',
                padding: '0.2rem 1rem 1rem',
                borderRadius: '8px',
                minHeight: '605px'
              }}>
                <h3>TOPICS</h3>

{/* Topics List */}
<div style={{ 
  display: 'flex', 
  flexDirection: 'column', 
  gap: '0.5rem',
  maxHeight: '585px',
  overflowY: 'auto',
  marginTop: '0rem',
  marginBottom: '1rem'
}}>
  {topics.map((topic, index) => (
    <button 
      key={index}
      onClick={() => handleTopicChange(topic)}
      className={selectedTopic === topic ? 'selected' : ''}
    >
      {topic}
    </button>
  ))}
</div>

{/* Scroll Indicator */}
<div style={{
  fontSize: '11px',
  color: '#666',
  fontStyle: 'italic',
  textAlign: 'center',
  padding: '0.5rem',
  marginTop: '0.5rem'
}}>
  Scroll for more
</div>
</div>
</div>
</>
)}

   {/* Fixed Bottom Navigation - Visible on scroll up */}
   <div style={{
     position: 'fixed',
     bottom: showBottomNav ? '20px' : '-80px',
     left: '50%',
     transform: 'translateX(-50%)',
     display: 'flex',
     justifyContent: 'center',
     alignItems: 'center',
     gap: '1rem',
     padding: '12px 24px',
     background: 'rgba(255, 255, 255, 0.25)',
     backdropFilter: 'blur(20px) saturate(180%)',
     WebkitBackdropFilter: 'blur(20px) saturate(180%)',
     borderRadius: '50px',
     border: '1px solid rgba(255, 255, 255, 0.4)',
     boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 2px 4px rgba(255, 255, 255, 0.5)',
     transition: 'bottom 0.3s ease',
     zIndex: 1000
   }}>
       
         <Link
           to="/saved"
           title="Saved items"
           style={{
             padding: 0,
             background: 'rgba(255, 255, 255, 0.2)',
             backdropFilter: 'blur(20px) saturate(200%)',
             WebkitBackdropFilter: 'blur(20px) saturate(200%)',
             color: '#666',
             border: '1px solid rgba(255, 255, 255, 0.5)',
             borderRadius: '50%',
             cursor: 'pointer',
             textDecoration: 'none',
             display: 'inline-flex',
             alignItems: 'center',
             justifyContent: 'center',
             width: '40px',
             height: '40px',
             transition: 'all 0.3s ease',
             boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -2px 4px rgba(0, 0, 0, 0.1)'
           }}
           onMouseEnter={(e) => {
             if (windowWidth >= 576) {
                
               e.currentTarget.style.backdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)';
               e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.6), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
             }
           }}
           onMouseLeave={(e) => {
              
             e.currentTarget.style.transform = 'translateY(0)';
             e.currentTarget.style.backdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
           }}
         >
           <FontAwesomeIcon icon={faHeart} style={{ fontSize: '16px' }} />
         </Link>

         {windowWidth < 1300 && (
           <button
             onClick={toggleFilters}
             title="Filters"
             style={{
               padding: 0,
               background: 'rgba(255, 255, 255, 0.2)',
               backdropFilter: 'blur(20px) saturate(200%)',
               WebkitBackdropFilter: 'blur(20px) saturate(200%)',
               color: '#666',
               border: '1px solid rgba(255, 255, 255, 0.5)',
               borderRadius: '50%',
               cursor: 'pointer',
               display: 'inline-flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '40px',
               height: '40px',
               transition: 'all 0.3s ease',
               boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -2px 4px rgba(0, 0, 0, 0.1)'
             }}
             onMouseEnter={(e) => {
               if (windowWidth >= 576) {
                  
                 e.currentTarget.style.backdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)';
                 e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.6), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
               }
             }}
             onMouseLeave={(e) => {
                
               e.currentTarget.style.transform = 'translateY(0)';
               e.currentTarget.style.backdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
             }}
           >
             <FontAwesomeIcon icon={faFilter} style={{ fontSize: '16px' }} />
           </button>
         )}

         <div style={{ position: 'relative', display: 'inline-block' }}>
           <button
             title={`Sort: ${sortOption === 'featured' ? 'Featured' : sortOption === 'newest' ? 'Newest' : 'Most Popular'}`}
             style={{
               padding: 0,
               background: 'rgba(255, 255, 255, 0.2)',
               backdropFilter: 'blur(20px) saturate(200%)',
               WebkitBackdropFilter: 'blur(20px) saturate(200%)',
               color: '#666',
               border: '1px solid rgba(255, 255, 255, 0.5)',
               borderRadius: '50%',
               cursor: 'pointer',
               display: 'inline-flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '40px',
               height: '40px',
               transition: 'all 0.3s ease',
               boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -2px 4px rgba(0, 0, 0, 0.1)'
             }}
             onClick={() => {
               const select = document.getElementById('editorials-sort-select');
               if (select) select.focus();
             }}
             onMouseEnter={(e) => {
               if (windowWidth >= 576) {
                  
                 e.currentTarget.style.backdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)';
                 e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.6), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
               }
             }}
             onMouseLeave={(e) => {
                
               e.currentTarget.style.transform = 'translateY(0)';
               e.currentTarget.style.backdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
             }}
           >
             <FontAwesomeIcon icon={faArrowsUpDown} style={{ fontSize: '16px' }} />
           </button>
           <select
             id="editorials-sort-select"
             value={sortOption}
             onChange={(e) => setSortOption(e.target.value)}
             style={{
               position: 'absolute',
               top: 0,
               left: 0,
               width: '40px',
               height: '40px',
               opacity: 0,
               cursor: 'pointer',
               borderRadius: '50%'
             }}
           >
             <option value="featured">Featured</option>
             <option value="newest">Newest</option>
             <option value="popular">Most Popular</option>
           </select>
         </div>
   </div>

   {/* Main editorial container */}
   <div className="editorials-main">
     {/* Search results indicator */}
     {selectedTopic !== 'All' && displayEditorials.length === 0 && (
       <div style={{
         padding: '3rem 1rem',
         textAlign: 'center',
         background: '#f9f9f9',
         borderRadius: '8px',
         marginBottom: '2rem'
       }}>
         <FontAwesomeIcon 
           icon={faNewspaper} 
           style={{ fontSize: '2.5rem', color: '#ccc', marginBottom: '1rem' }}
         />
         <h3 style={{ margin: '0 0 0.5rem', color: '#555' }}>No editorials found</h3>
         <p style={{ margin: '0 0 1rem', color: '#777' }}>
           {searchQuery ? `No editorials found for "${searchQuery}". Try different search terms.` : `No editorials found for "${selectedTopic}". Try a different topic.`}
         </p>
         <button
           onClick={() => {
             setSearchQuery('');
             setSelectedTopic('All');
             const newUrl = new URL(window.location);
             newUrl.searchParams.delete('q');
             navigate(newUrl.pathname + newUrl.search, { replace: true });
           }}
           style={{
             padding: '0.5rem 1rem',
             background: '#0d47a1',
             color: 'white',
             border: 'none',
             borderRadius: '4px',
             cursor: 'pointer'
           }}
         >
           Reset Filters
         </button>
       </div>
     )}

     {/* Two Column Editorial Feed Layout */}
     <div className="editorials-feed">
       {visibleEditorials.map((editorial, index) => (
         <article 
           key={`${editorial.id}-${index}`} 
           className="editorials-card fade-in"
           ref={index === visibleEditorials.length - 1 ? lastEditorialRef : null}
           style={{
             position: 'relative',
             backgroundColor: 'white',
             cursor: 'pointer'
           }}
           onClick={(e) => handleReadMore(e, editorial)}
         >
           {/* Image container */}
           <div style={{
             position: 'relative',
             width: '100%',
             height: '450px',
             overflow: 'hidden',
             backgroundColor: '#f5f5f5',
             marginBottom: '1.5rem'
           }}>
             <img 
               src={editorial.image}
               alt={editorial.title || 'Editorial'}
               style={{ 
                 width: '100%',
                 height: '100%', 
                 objectFit: 'cover',
                 display: 'block'
               }}
               onError={(e) => { e.target.src = fallbackImage }}
             />
             
             {/* Search score indicator (debug mode) */}
             {debug && (editorial.searchScore || editorial.preferenceScore) && (
               <div style={{
                 position: 'absolute',
                 top: '8px',
                 left: '8px',
                 background: 'rgba(0,0,0,0.7)',
                 color: 'white',
                 padding: '2px 6px',
                 borderRadius: '4px',
                 fontSize: '0.7rem',
                 fontWeight: 'bold'
               }}>
                 {editorial.searchScore ? editorial.searchScore.toFixed(0) : editorial.preferenceScore.toFixed(2)}
               </div>
             )}
             
             {/* Revenue potential indicator (debug mode) */}
             {debug && editorial.commerceIntegration?.expectedRevenue && (
               <div style={{
                 position: 'absolute',
                 bottom: '8px',
                 left: '8px',
                 background: 'rgba(0,128,0,0.8)',
                 color: 'white',
                 padding: '2px 6px',
                 borderRadius: '4px',
                 fontSize: '0.7rem',
                 fontWeight: 'bold'
               }}>
                 ${editorial.commerceIntegration.expectedRevenue.total}
               </div>
             )}
             
             {/* Action buttons */}
             <div 
               style={{
                 position: 'absolute',
                 top: '15px',
                 right: '15px',
                 display: 'flex',
                 gap: '8px'
               }}
             >
               <button 
                 onClick={(e) => handleSave(e, editorial)}
                 style={{
                   width: '27.2px',
                   height: '27.2px',
                   borderRadius: '50%',
                   background: 'rgba(255, 255, 255, 0.8)',
                   border: 'none',
                   display: 'flex',
                   justifyContent: 'center',
                   alignItems: 'center',
                   cursor: 'pointer',
                   boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                 }}
                 title="Save"
               >
                 <FontAwesomeIcon
                   icon={faBookmark}
                   style={{ color: isSaved && isSaved(editorial.id) ? '#000080' : '#666' }}
                 />
               </button>
               
               <button 
                 onClick={(e) => handleShare(e, editorial)}
                 style={{
                   width: '27.2px',
                   height: '27.2px',
                   borderRadius: '50%',
                   background: 'rgba(255, 255, 255, 0.8)',
                   border: 'none',
                   display: 'flex',
                   justifyContent: 'center',
                   alignItems: 'center',
                   cursor: 'pointer',
                   boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                 }}
                 title="Share"
               >
                 <FontAwesomeIcon
                   icon={faPaperPlane}
                   style={{ color: '#666' }}
                 />
               </button>
             </div>
           </div>
           
           {/* Content below image with editorial-style separators */}
           <div className="editorial-card-details">
             <div className="editorial-card-author">
               {editorial.author ? `BY ${editorial.author.toUpperCase()}` : 'EDITORIAL'}
             </div>
             <div className="editorial-separator-line"></div>
             <h3 className="editorial-card-title">
               {editorial.title || 'Untitled Editorial'}
             </h3>
             <div className="editorial-separator-dots"></div>
             <div className="editorial-card-date">
               {editorial.date || 'Date'}
             </div>
           </div>

           {/* Keep remaining content below separators */}
           <div style={{ paddingBottom: '1rem', paddingTop: '0.5rem' }}>
             {/* Topic badge */}
             {editorial.topic && (
               <div style={{ marginBottom: '0.75rem' }}>
                 <span style={{
                   display: 'inline-block',
                   padding: '0.25rem 0.75rem',
                   fontSize: '0.7rem',
                   fontWeight: '600',
                   backgroundColor: '#f0f0f0',
                   color: '#666',
                   borderRadius: '12px',
                   textTransform: 'uppercase',
                   letterSpacing: '0.5px'
                 }}>
                   {editorial.topic}
                 </span>
               </div>
             )}

             {/* Description */}
             <div style={{
               margin: '0 0 1.25rem',
               fontSize: '0.85rem',
               lineHeight: '1.6',
               color: '#888'
             }}>
               {editorial.description}
             </div>

             {/* Affiliate disclosure - hide for specific editorials */}
             {!editorial.hideAffiliateDisclosure && (
               <p style={{
                 margin: '0 0 1rem',
                 fontSize: '0.8rem',
                 color: '#666',
                 fontStyle: 'italic'
               }}>
                 This post may contain affiliate links.
               </p>
             )}
             
             {/* Debug scores and revenue info */}
             {debug && (
               <div style={{ 
                 margin: '0 0 1rem', 
                 fontSize: '0.8rem', 
                 color: '#0d47a1',
                 fontWeight: 600,
                 background: '#f8f9fa',
                 padding: '0.5rem',
                 borderRadius: '4px'
               }}>
                 {editorial.searchScore && (
                   <p style={{ margin: 0 }}>
                     Search Score: {editorial.searchScore.toFixed(1)}
                   </p>
                 )}
                 {editorial.preferenceScore && (
                   <p style={{ margin: 0 }}>
                     Preference Score: {editorial.preferenceScore.toFixed(2)}
                   </p>
                 )}
                 {editorial.commerceIntegration?.expectedRevenue && (
                   <p style={{ margin: 0 }}>
                     Expected Revenue: ${editorial.commerceIntegration.expectedRevenue.total}
                     (Affiliate: ${editorial.commerceIntegration.expectedRevenue.affiliate})
                   </p>
                 )}
               </div>
             )}
             
             {/* Tags */}
             {editorial.tags && editorial.tags.length > 0 && (
               <div style={{ marginBottom: '1.25rem' }}>
                 {editorial.tags.slice(0, 5).map((tag, tagIndex) => (
                   <span key={tagIndex} style={{
                     display: 'inline-block',
                     padding: '0.25rem 0.6rem',
                     margin: '0 0.5rem 0.5rem 0',
                     fontSize: '0.8rem',
                     backgroundColor: '#f8f9fa',
                     color: '#495057',
                     borderRadius: '4px',
                     border: '1px solid #e9ecef'
                   }}>
                     {tag}
                   </span>
                 ))}
                 {editorial.tags.length > 5 && (
                   <span style={{
                     fontSize: '0.8rem',
                     color: '#666',
                     fontStyle: 'italic'
                   }}>
                     +{editorial.tags.length - 5} more
                   </span>
                 )}
               </div>
             )}
             
             {/* Action buttons */}
             <div style={{
               display: 'flex',
               gap: '0.5rem',
               alignItems: 'center',
               marginBottom: '1.5rem'
             }}>
               <button 
                 onClick={(e) => handleSave(e, editorial)}
                 style={{
                   padding: '0.25rem 1rem',
                   fontSize: '11px',
                   border: isSaved && isSaved(editorial.id) ? '1px solid #0d47a1' : '1px solid transparent',
                   borderRadius: '4px',
                   cursor: 'pointer',
                   backgroundColor: 'transparent',
                   color: isSaved && isSaved(editorial.id) ? '#0d47a1' : '#333',
                   transition: 'all 0.2s ease'
                 }}
                 onMouseEnter={(e) => {
                   if (!(isSaved && isSaved(editorial.id))) {
                     e.target.style.backgroundColor = '#f5f5f5';
                   }
                 }}
                 onMouseLeave={(e) => {
                   if (!(isSaved && isSaved(editorial.id))) {
                     e.target.style.backgroundColor = 'transparent';
                   }
                 }}
               >
                 {isSaved && isSaved(editorial.id) ? 'Saved' : 'Save'}
               </button>
               
               <button 
                 onClick={(e) => handleReadMore(e, editorial)}
                 style={{
                   padding: '0.25rem 1rem',
                   fontSize: '11px',
                   border: '1px solid transparent',
                   borderRadius: '4px',
                   cursor: 'pointer',
                   backgroundColor: 'transparent',
                   color: '#333',
                   transition: 'all 0.2s ease'
                 }}
                 onMouseEnter={(e) => {
                   e.target.style.backgroundColor = '#f5f5f5';
                 }}
                 onMouseLeave={(e) => {
                   e.target.style.backgroundColor = 'transparent';
                 }}
               >
                 Read More
               </button>
               
               {!editorial.hideShopButton && (
                 <button 
                   onClick={(e) => {
                     e.preventDefault();
                     e.stopPropagation();
                     
                     if (editorial.affiliateUrl) {
                       window.open(editorial.affiliateUrl, '_blank');
                       
                       trackEditorialCommerce(editorial.id, editorial.amazonAsin, 'amazon_affiliate_click', {
                         userId: user?.uid || 'anonymous',
                         source: 'editorial_shop_button',
                         asin: editorial.amazonAsin
                       });
                     }
                   }}
                   style={{
                     padding: '0.25rem 1rem',
                     fontSize: '11px',
                     border: '1px solid transparent',
                     borderRadius: '4px',
                     cursor: 'pointer',
                     backgroundColor: 'transparent',
                     color: '#333',
                     transition: 'all 0.2s ease',
                     display: 'flex',
                     alignItems: 'center',
                     gap: '0.5rem'
                   }}
                   onMouseEnter={(e) => {
                     e.target.style.backgroundColor = '#f5f5f5';
                   }}
                   onMouseLeave={(e) => {
                     e.target.style.backgroundColor = 'transparent';
                   }}
                 >
                   <FontAwesomeIcon icon={faShoppingBag} style={{ fontSize: '0.8rem' }} />
                   {editorial.customButtonText || 'Buy Now'}
                 </button>
               )}
             </div>
           </div>
         </article>
       ))}
     </div>
   </div>
   
   {/* RIGHT SIDEBAR  */}
   {windowWidth >= 1300 && (
     <>
       {/* Right Sidebar Video Background  */}
       <div style={{
         position: 'fixed',
         right: 0,
         top: 0,
         width: '210px',
         height: '100vh',
         zIndex: 1,
         overflow: 'hidden'
       }}>
         <video
           ref={rightVideoRef}
           src="/videos/sidebar-column.mov"
           style={{
             width: '100%',
             height: '100%',
             objectFit: 'cover',
             objectPosition: 'center'
           }}
           muted
           loop={false}
           playsInline
           preload="auto"
           autoPlay
           onLoadedData={() => {
             if (rightVideoRef.current) {
               setupBoomerangEffect(rightVideoRef.current);
             }
           }}
           onError={(e) => {
             console.error('Right video error:', e);
           }}
         />
       </div>

       {/* Right Sidebar Content */}
       <div className="editorials-right-sidebar" style={{
         position: 'sticky',
         top: '140px',
         width: '190px',
         height: '650px',
         marginRight: 'calc(20px - 50vw + 50%)',
         zIndex: 2,
         backdropFilter: 'blur(3px)',
         WebkitBackdropFilter: 'blur(3px)'
       }}>
         {/* Featured Editorials Section */}
         <div style={{
           position: 'relative',
           zIndex: 2,
           background: 'rgba(255, 255, 255, 0.3)',
           marginLeft: '10px',
           marginRight: '10px',
           marginTop: '10px',
           marginBottom: '10px',
           padding: '1rem 1rem 1rem',
           borderRadius: '8px',
           minHeight: '450px'
         }}>
           <h3>FEATURED EDITORIALS</h3>

<div style={{ 
  display: 'flex', 
  flexDirection: 'column', 
  gap: '0.5rem',
  maxHeight: '400px',
  overflowY: 'auto',
  marginTop: '0rem',
  marginBottom: '1rem'
}}>
  {featuredEditorials.slice(0, 3).map((editorial) => (
    <button 
      key={editorial.id}
      onClick={() => navigate(`/editorials/${editorial.id}`)}
      className=""
    >
      {editorial.title.length > 30 ? editorial.title.substring(0, 30) + '...' : editorial.title}
    </button>
  ))}
</div>

<div style={{
  fontSize: '11px',
  color: '#666',
  fontStyle: 'italic',
  textAlign: 'center',
  padding: '0.5rem',
  marginTop: '0.5rem'
}}>
  Scroll for more
</div>
</div>

         {/* Footer Section */}
<div className="sidebar-footer" style={{
  position: 'relative',
  zIndex: 2,
  background: 'rgba(255, 255, 255, 0.3)',
  borderRadius: '8px',
  padding: '1rem',
  marginLeft: '10px',
  marginRight: '10px',
  marginTop: '10px',
  marginBottom: '20px'
}}>
  <div className="footer-links" style={{
    transform: 'translateX(20px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    fontSize: '0.75rem',
    color: '#666'
  }}>
    <Link to="/about" style={{ color: '#666', textDecoration: 'none' }}>About</Link>
    <Link to="/help" style={{ color: '#666', textDecoration: 'none' }}>Help</Link>
    <Link to="/terms" style={{ color: '#666', textDecoration: 'none' }}>Terms</Link>
    <Link to="/privacy" style={{ color: '#666', textDecoration: 'none' }}>Privacy</Link>
  </div>
  <div className="copyright" style={{
    transform: 'translateX(15px)',
    marginTop: '0.5rem',
    fontSize: '0.7rem',
    color: '#666'
  }}>
    © Artifex {new Date().getFullYear()}
  </div>
</div>
</div>
</>
)}
</div>
</>
);
};

export default Editorials;